package org.moxultimate.topwithmox.productcrud.update

import androidx.compose.runtime.*
import com.varabyte.kobweb.compose.css.FontWeight
import com.varabyte.kobweb.compose.css.Overflow
import com.varabyte.kobweb.compose.css.functions.clamp
import com.varabyte.kobweb.compose.foundation.layout.Arrangement
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.graphics.Colors
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.silk.components.text.SpanText
import com.varabyte.kobweb.silk.style.toModifier
import org.jetbrains.compose.web.css.cssRem
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.css.vw
import org.moxultimate.topwithmox.components.buttons.GradientButton
import org.moxultimate.topwithmox.components.buttons.PressableButtonStyle
import org.moxultimate.topwithmox.components.crud.CRUDStatusBanner
import org.moxultimate.topwithmox.components.crud.CRUDSuccessDialog
import org.moxultimate.topwithmox.components.loading.LoadingIndicator
import org.moxultimate.topwithmox.components.texts.moxTextGradient
import org.moxultimate.topwithmox.di.koinViewModel
import org.moxultimate.topwithmox.navigation.rememberAppNavigator
import org.moxultimate.topwithmox.productcrud.add.ProductFormFields
import org.moxultimate.topwithmox.productcrud.main.ProductCRUDState
import org.moxultimate.topwithmox.productcrud.main.ProductCRUDViewModel
import org.moxultimate.topwithmox.productcrud.main.ProductFormEntry
import org.moxultimate.topwithmox.productcrud.main.toFormEntry
import org.moxultimate.topwithmox.theme.FontFamily

/**
 * ProductItemUpdatePage
 * Edit page for a single product entry identified by [productId] within content [contentId].
 * Fetches the full product list on open, pre-populates the form, then calls
 * PUT /products/{productId} on save or DELETE /products/{productId} on delete.
 *
 * @param contentId Parent content ID — used to load the product list.
 * @param productId The specific product to edit.
 */
@Composable
fun ProductItemUpdatePage(contentId: Long, productId: Long) {
    val viewModel: ProductCRUDViewModel = koinViewModel()
    val productOpState by viewModel.productOpState.collectAsState()
    val contentProducts by viewModel.contentProducts.collectAsState()
    val isLoadingProducts by viewModel.isLoadingProducts.collectAsState()
    val navigator = rememberAppNavigator()

    LaunchedEffect(contentId) {
        viewModel.loadContentById(contentId)
    }

    val product = contentProducts.find { it.id == productId }

    // Initialise form once the product is available
    var entry by remember { mutableStateOf<ProductFormEntry?>(null) }
    LaunchedEffect(product) {
        if (product != null && entry == null) {
            entry = product.toFormEntry()
        }
    }

    if (productOpState is ProductCRUDState.Success) {
        CRUDSuccessDialog(
            message = (productOpState as ProductCRUDState.Success).message,
            onOkay = {
                viewModel.resetProductState()
                navigator.goToProductCRUD()
            },
        )
    }

    Box(
        modifier = Modifier
            .fillMaxWidth()
            .fillMaxHeight()
            .backgroundColor(Colors.White)
            .overflow(Overflow.Auto),
        contentAlignment = Alignment.TopCenter,
    ) {
        Column(
            modifier = Modifier
                .width(clamp(min = 700.px, value = 70.vw, max = 1100.px))
                .padding(top = 80.px, bottom = 48.px, leftRight = 2.cssRem),
            verticalArrangement = Arrangement.spacedBy(28.px),
        ) {
            // Header row
            Row(
                modifier = Modifier.fillMaxWidth(),
                horizontalArrangement = Arrangement.SpaceBetween,
                verticalAlignment = Alignment.CenterVertically,
            ) {
                Column(verticalArrangement = Arrangement.spacedBy(4.px)) {
                    SpanText(
                        text = "Edit Product",
                        modifier = Modifier
                            .fontFamily(FontFamily)
                            .fontSize(32.px)
                            .fontWeight(FontWeight.Bold)
                            .moxTextGradient(),
                    )
                    SpanText(
                        text = "Product ID: $productId  ·  Content ID: $contentId",
                        modifier = Modifier
                            .fontFamily(FontFamily)
                            .fontSize(13.px)
                            .color(Colors.Gray),
                    )
                }
                SpanText(
                    text = "← Back",
                    modifier = PressableButtonStyle.toModifier()
                        .fontFamily(FontFamily)
                        .fontSize(14.px)
                        .color(Colors.Gray)
                        .onClick { navigator.goBack() },
                )
            }

            // Status banner
            if (productOpState is ProductCRUDState.Error) {
                CRUDStatusBanner(
                    message = (productOpState as ProductCRUDState.Error).message,
                    isError = true,
                    onDismiss = { viewModel.resetProductState() },
                )
            }

            when {
                isLoadingProducts -> Box(
                    modifier = Modifier.fillMaxWidth().padding(topBottom = 48.px),
                    contentAlignment = Alignment.Center,
                ) { LoadingIndicator() }

                entry == null -> SpanText(
                    text = "Product not found (ID: $productId).",
                    modifier = Modifier
                        .fontFamily(FontFamily)
                        .fontSize(14.px)
                        .color(Colors.Gray),
                )

                else -> {
                    val currentEntry = entry!!

                    ProductFormFields(
                        entry = currentEntry,
                        onEntryChange = { entry = it },
                    )

                    val isLoading = productOpState is ProductCRUDState.Loading
                    Row(
                        modifier = Modifier.fillMaxWidth(),
                        horizontalArrangement = Arrangement.spacedBy(16.px),
                        verticalAlignment = Alignment.CenterVertically,
                    ) {
                        GradientButton(
                            text = if (isLoading) "Saving…" else "Save Changes",
                            disabled = !currentEntry.isValidForUpdate || isLoading,
                            onClick = {
                                if (currentEntry.isValidForUpdate && !isLoading) {
                                    viewModel.updateProductItem(productId, currentEntry)
                                }
                            },
                        )
                        GradientButton(
                            text = if (isLoading) "Please wait…" else "🗑 Delete",
                            disabled = isLoading,
                            onClick = { if (!isLoading) viewModel.deleteProductItem(productId) },
                        )
                    }
                }
            }
        }
    }
}
